import { Food, FoodHeader } from '@cf-shared/data-model/food';
import { TranslatedString } from 'models/translated-string';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { ScannedFoodMatchSource } from 'services/scanner-food-match';
import { MealPhotoScannerMatchedFood } from './meal-photo-scanner-interface';

/**
 * One row of the meal photo scanner's result list: a food the scan named, or one the user added by
 * hand because the scan missed it (#6807).
 *
 * Kept apart from the page, like `meal-scan-running-total.ts`, so the rules below can be
 * unit-tested without standing up the whole Angular page.
 */
export interface MealScanResultRow {
  // identifies this row. Minted per row, because aiName does NOT identify one: two
  // of the same food on one plate come back as two rows with the same name, and
  // keying off the name wrote one row's edit onto the other. See
  // services/scanner-row-identity.
  rowId: string;
  aiName: string;            // the name the scan gave; empty for a row the user added by hand
  aiEstimatedGrams: number;  // original AI estimate (never changes)
  estimatedGrams: number;    // user-adjusted value (updated via edit ingredient)
  confidence: number;
  matched: boolean;
  checked: boolean;
  foodHeader: FoodHeader;  // matched DB food or placeholder
  food?: Food;             // the full matched food, loaded so the row's numbers can be scaled to its grams
  nutrients?: UiDisplayNutrient[];  // what the row's grams hold; empty when the food could not be loaded (#6806)
  subtitle: TranslatedString;
  foodId?: string;         // only set when matched
  matchSource: ScannedFoodMatchSource;  // where the row's food came from, for telemetry (#6656)
  weighedWithRefuse?: { percent: number };  // the "Weighed with peel" tick from the portion screen (#5544)
}

/**
 * The row for a food the user added by hand: ticked, holding the amount they saved on the portion
 * screen. It has no AI name, because the scan never named this food.
 */
export function buildHandAddedRow(args: {
  rowId: string;
  food: FoodHeader;
  grams: number;
  weighedWithRefuse?: { percent: number };
  subtitle: TranslatedString;
}): MealScanResultRow {
  return {
    rowId: args.rowId,
    aiName: '',
    aiEstimatedGrams: args.grams,
    estimatedGrams: args.grams,
    // the user chose this food themselves, so there is no doubt about it to record
    confidence: 1,
    matched: true,
    checked: true,
    foodHeader: args.food,
    subtitle: args.subtitle,
    foodId: args.food._id,
    matchSource: ScannedFoodMatchSource.UserAdded,
    ...(args.weighedWithRefuse ? { weighedWithRefuse: args.weighedWithRefuse } : {}),
  };
}

/** whether the user added this row by hand rather than the scan naming it */
export function isHandAddedRow(row: MealScanResultRow): boolean {
  return row.matchSource === ScannedFoodMatchSource.UserAdded;
}

/** the rows that go into the meal: ticked and holding a food */
function rowsGoingIntoTheMeal(rows: MealScanResultRow[]): MealScanResultRow[] {
  return rows.filter(row => row.checked && row.matched && row.foodId);
}

/**
 * The rows whose choice the preference memory should remember. The memory maps a name the scan gave
 * to the food the user kept for it, so a row the user added by hand, which has no such name, is left
 * out: filed under its own food name it would be an entry no scanned name ever looks up.
 */
export function rowsThatTeachThePreferenceMemory(rows: MealScanResultRow[]): MealScanResultRow[] {
  return scannedRowsGoingIntoTheMeal(rows);
}

/**
 * The rows going into the meal that the scan named, leaving out the ones the user added by hand.
 * Those have no scan estimate, so they must also stay out of the scan-grams-versus-saved-grams
 * measurement (#6808): a hand-added food the user re-weighs is not a correction of the scan.
 */
export function scannedRowsGoingIntoTheMeal(rows: MealScanResultRow[]): MealScanResultRow[] {
  return rowsGoingIntoTheMeal(rows).filter(row => !isHandAddedRow(row));
}

/** what the scanner hands back to the meal: every ticked row with a food, hand-added rows included */
export function scanRowsToMatchedFoods(rows: MealScanResultRow[]): MealPhotoScannerMatchedFood[] {
  return rowsGoingIntoTheMeal(rows).map(row => ({
    aiName: row.aiName,
    estimatedGrams: row.estimatedGrams,
    confidence: row.confidence,
    food: row.foodHeader,
    ...(row.weighedWithRefuse ? { weighedWithRefuse: row.weighedWithRefuse } : {}),
  }));
}
